import { useMemo } from 'react';

import { getMarginRatio } from '#/api/apiUtils';
import { useInitialMarginFractionOverrideQueries } from '#/api/queries/initialMarginFractionOverrideQueries';
import { useSelector } from '#/reduxState/storeCore';

/** Wallet specific IMF override for market */
export const useFetchInitialMarginFractionOverride = ({
  walletAddress,
  isVault,
  market,
}: {
  walletAddress: string;
  isVault: boolean;
  market: string | undefined;
}) => {
  const { data: initialMarginFractionOverrides } =
    useInitialMarginFractionOverrideQueries({ walletAddress, isVault });
  const initialMarginFractionOverride = useMemo(() => {
    if (!market || !initialMarginFractionOverrides) {
      // Not yet initialized
      return undefined;
    }
    if (!walletAddress) {
      return null;
    }
    const overrideMarket = initialMarginFractionOverrides?.find(
      override => override.market === market
    );
    return overrideMarket?.initialMarginFractionOverride;
  }, [market, initialMarginFractionOverrides, walletAddress]);
  return initialMarginFractionOverride;
};

/** Fetch IMF for wallet on given market */
export const useFetchInitialMarginFractionForWallet = (params: {
  walletAddress: string;
  isVault: boolean;
  market: string | undefined;
}): string | undefined => {
  const initialMarginFractionOverride =
    useFetchInitialMarginFractionOverride(params);

  const marketsLeverageParams = useSelector(
    state => state.markets.listedMarkets.marketsLeverageParams
  );
  const marketInitialMarginFraction = params.market
    ? marketsLeverageParams?.[params.market]?.initialMarginFraction
    : undefined;

  const initialMarginFraction = useMemo(() => {
    if (
      initialMarginFractionOverride === undefined ||
      !marketInitialMarginFraction
    ) {
      return undefined;
    }
    return getMarginRatio(
      marketInitialMarginFraction,
      initialMarginFractionOverride
    );
  }, [initialMarginFractionOverride, marketInitialMarginFraction]);
  return initialMarginFraction;
};
